import React, { Fragment, useRef } from 'react';
import { observer } from 'mobx-react';
import { Input } from 'cd-eft/components/common/Input';
import { ChannelFlowVM } from './ChannelFlowFormVM';
import { RadioGroup } from 'cd-eft/components/common/RadioGroup';
import 'uswds/dist/css/uswds.css';
import './channelFlowForm.scss';
import { WaterwaySizingCalculatorVM } from '../WaterwaySizingCalculatorVM';

interface ISubFormProps {
    vm: ChannelFlowVM;
    mode:string;
}

interface IChannelFormProps {
    vm: WaterwaySizingCalculatorVM;
}
interface IComboBox {
    label?: string;
    idAndName: string;
    initValue?: any;
    valid: boolean;
    required?: boolean;
    value: any;
    onChange: (event: {}) => void;
    options: IOption[];
    errorMsg: string;
    popupCallback: () => void;
}
interface IOption {
    name: string;
    value: any;
}

const TrapezoidalForm = observer((props: ISubFormProps) => {
    return (
        <Fragment>
            <div className="row">
                <Input
                    idAndName="left-slope"
                    label="Left Slope (ft/ft)"
                    valid={!props.vm.formValidation.leftSlope}
                    value={props.vm.trapForm.leftSlope}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleTrapInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    required={true}
                    autofocus={false}
                    className={props.vm.formClassName.leftSlope}
                />
                <Input
                    idAndName="right-slope"
                    label="Right Slope (ft/ft)"
                    valid={!props.vm.formValidation.rightSlope}
                    value={props.vm.trapForm.rightSlope}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleTrapInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    required={true}
                    className={props.vm.formClassName.rightSlope}
                />
                <Input
                    idAndName="min-bed-width"
                    label="Minimum Bed Width (ft)"
                    valid={!props.vm.formValidation.minBedWidth}
                    value={props.vm.trapForm.minBedWidth}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleTrapInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    required={true}
                    className={props.vm.formClassName.minBedWidth}
                />
            </div>
            {/*Removed Dip Section Here June 9th, 2023 in NETS-2371*/}
            {props.mode !== 'adminGlobalPreferences'?
            <div className="row freeboard-row">
                <Input
                    idAndName="freeboard1"
                    label="Freeboard (ft) (optional)"
                    valid={!props.vm.formValidation.freeboard1}
                    value={props.vm.trapForm.freeboard}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleTrapInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    className={props.vm.formClassName.freeboard1}
                    disabled={props.mode === "design"}
                />
            </div> : null}
        </Fragment>
    );
});

const ParabolicForm = observer((props: ISubFormProps) => {
    return (
        <Fragment>

            {props.mode !== 'adminGlobalPreferences' ?
            <div className="row freeboard-row">
                <Input
                    idAndName="freeboard2"
                    label="Freeboard (ft) (optional)"
                    valid={!props.vm.formValidation.freeboard2}
                    value={props.vm.parabolicForm.freeboard}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleParaInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    autofocus={false}
                    className={props.vm.formClassName.freeboard2}
                    disabled={props.mode === "design"}
                />
            </div> : null}
            <div className="row">
                <Input
                    idAndName="limiting-slope"
                    label="Limiting Slope (ft/ft)"
                    valid={!props.vm.formValidation.limitingSlope}
                    value={props.vm.parabolicForm.limitingSlope}
                    type="number"
                    step={'0.01'}
                    onChange={props.vm.handleParaInputChange}
                    onBlur={props.vm.commonValidation}
                    onKeyDown={props.vm.handleOnKeyDown}
                    required={true}
                    className={props.vm.formClassName.limitingSlope}
                />
                <span className="offset-group">
                    <Input
                        idAndName="offset-1"
                        label="Offset 1 (%)"
                        valid={true}
                        value={props.vm.parabolicForm.offset[0]}
                        type="number"
                        step={'0.01'}
                        onChange={props.vm.handleParaInputChange}
                        onBlur={props.vm.commonValidation}
                        onKeyDown={props.vm.handleOnKeyDown}
                        required={true}
                        className={props.vm.formClassName.offset1}
                    />
                    <Input
                        idAndName="offset-2"
                        label="Offset 2 (%)"
                        valid={true}
                        value={props.vm.parabolicForm.offset[1]}
                        type="number"
                        step={'0.01'}
                        onChange={props.vm.handleParaInputChange}
                        onBlur={props.vm.commonValidation}
                        onKeyDown={props.vm.handleOnKeyDown}
                        required={true}
                        className={props.vm.formClassName.offset2}
                    />
                    <Input
                        idAndName="offset-3"
                        label="Offset 3 (%)"
                        valid={true}
                        value={props.vm.parabolicForm.offset[2]}
                        type="number"
                        step={'0.01'}
                        onChange={props.vm.handleParaInputChange}
                        onBlur={props.vm.commonValidation}
                        onKeyDown={props.vm.handleOnKeyDown}
                        required={true}
                        className={props.vm.formClassName.offset3}
                    />
                </span>
            </div>
        </Fragment>
    );
});

const FormContainer = (props: IChannelFormProps) => {
    const vm = useRef(props.vm.channelFlowVM).current;
    const mode = useRef(props.vm.mode).current;
    if (!vm) {
        return <></>;
    }
    return (
        <div className="cff-container col">
            {props.vm.mode !== 'adminGlobalPreferences'?
            <>
            <div className="row">
                <Input
                    idAndName="channel-bed-slope"
                    label="Channel Bed Slope (%)"
                    valid={!vm.formValidation.channelBedSlope}
                    value={vm.channelBedSlope}
                    type="number"
                    step={'0.01'}
                    onChange={vm.handleInputChange}
                    onBlur={vm.commonValidation}
                    onKeyDown={vm.handleOnKeyDown}
                    required={true}
                    className={vm.formClassName.channelBedSlope}
                    disabled={props.vm.mode === "design"}
                />
                <Input
                    idAndName="design-discharge"
                    label="Design Discharge (cfs)"
                    valid={!vm.formValidation.designDischarge}
                    value={vm.designDischarge}
                    type="number"
                    step={'0.01'}
                    onChange={vm.handleInputChange}
                    onBlur={vm.commonValidation}
                    onKeyDown={vm.handleOnKeyDown}
                    required={true}
                    className={vm.formClassName.designDischarge}
                    disabled={props.vm.mode === "design"}
                />
            </div> 
            <hr />
            </> 
            : null}
            <div className="row control-radio-group channel-control-group">
                <h3 className="wsc-section-header" tabIndex={0}>Channel Controls</h3>
                <RadioGroup
                    labelId="channel-controls"
                    items={[
                        {
                            id: 'trapezoid',
                            name: 'trapezoid',
                            label: 'Trapezoidal',
                            className: 'control-radio-button usa-radio__input--tile',
                            onChange: vm.selectControlType,
                            checked: vm.trapezoidControl,
                            disabled: props.vm.mode === "design" && vm.parabolicControl
                        },
                        {
                            id: 'parabolic',
                            name: 'parabolic',
                            label: 'Parabolic',
                            className: 'control-radio-button usa-radio__input--tile',
                            onChange: vm.selectControlType,
                            checked: vm.parabolicControl,
                            disabled: props.vm.mode === "design" && vm.trapezoidControl
                        },
                    ]}
                />
            </div>
            <div className="">{vm.trapezoidControl ? <TrapezoidalForm vm={vm} mode={mode} /> : <ParabolicForm vm={vm} mode={mode}/>}</div>
            {props.vm.mode !== 'adminGlobalPreferences'? 
            <>
            <hr />
            <div className="row control-radio-group">
                <h3 className="wsc-section-header" tabIndex={0}>Channel Options</h3>
                <RadioGroup
                    labelId="channel-options"
                    items={[
                        {
                            id: 'not-defined',
                            name: 'not-defined',
                            label: 'Not User Defined',
                            className: 'control-radio-button usa-radio__input--tile',
                            onChange: vm.selectOptionType,
                            checked: vm.noneDefined,
                        },
                        {
                            id: 'fix-flow-depth',
                            name: 'fix-flow-depth',
                            label: 'Fix Flow Depth (ft)',
                            className: 'control-radio-button usa-radio__input--tile',
                            onChange: vm.selectOptionType,
                            checked: vm.fixFlowDepth,
                        },
                        {
                            id: 'fix-flow-width',
                            name: 'fix-flow-width',
                            label: 'Fix Flow Width (ft)',
                            className: 'control-radio-button usa-radio__input--tile',
                            onChange: vm.selectOptionType,
                            checked: vm.fixFlowWidth,
                        },
                    ]}
                />
            </div>
            <div className="channel-options-inputs row">
                {vm.fixFlowDepth ? (
                    <Input
                        idAndName="flow-depth-input"
                        label='Fix Flow Depth (ft)'
                        valid={!vm.formValidation.fixFlowDepth}
                        value={vm.channelOptions.fixFlowDepth}
                        type="number"
                        step={'0.01'}
                        onChange={vm.handleInputChange}
                        onBlur={vm.commonValidation}
                        onKeyDown={vm.handleOnKeyDown}
                        autofocus={false}
                        className={vm.formClassName.fixFlowDepth}
                    />
                ) : null}
                {vm.fixFlowWidth ? (
                    <Input
                        idAndName="flow-width-input"
                        label='Fix Flow Width (ft)'
                        valid={!vm.formValidation.fixFlowWidth}
                        value={vm.channelOptions.fixFlowWidth}
                        type="number"
                        step={'0.01'}
                        onChange={vm.handleInputChange}
                        onBlur={vm.commonValidation}
                        onKeyDown={vm.handleOnKeyDown}
                        autofocus={false}
                        className={vm.formClassName.fixFlowWidth}
                    />
                ) : null}
            </div> </>: null }
        </div>
    );
};



export const ChannelFlowCalculator = observer(FormContainer);
